import * as React from 'react';
import PropTypes from 'prop-types';
import * as AlertDialogPrimitive from '@radix-ui/react-alert-dialog';

import { cn } from '@/lib/utils';
import { buttonVariants } from '@/components/ui/button';

const AlertDialog = React.forwardRef(({ children, ...props }, ref) => (
  <AlertDialogPrimitive.Root ref={ref} {...props}>
    {children}
  </AlertDialogPrimitive.Root>
));
AlertDialog.displayName = 'AlertDialog';
AlertDialog.propTypes = {
  children: PropTypes.node,
  open: PropTypes.bool,
  onOpenChange: PropTypes.func,
  defaultOpen: PropTypes.bool,
};

const AlertDialogTrigger = React.forwardRef(({ children, ...props }, ref) => (
  <AlertDialogPrimitive.Trigger ref={ref} {...props}>
    {children}
  </AlertDialogPrimitive.Trigger>
));
AlertDialogTrigger.displayName = 'AlertDialogTrigger';
AlertDialogTrigger.propTypes = {
  children: PropTypes.node,
  asChild: PropTypes.bool,
};

const AlertDialogPortal = React.forwardRef(({ children, ...props }, ref) => (
  <AlertDialogPrimitive.Portal ref={ref} {...props}>
    {children}
  </AlertDialogPrimitive.Portal>
));
AlertDialogPortal.displayName = 'AlertDialogPortal';
AlertDialogPortal.propTypes = {
  children: PropTypes.node,
  container: PropTypes.instanceOf(Element),
  forceMount: PropTypes.bool,
};

const AlertDialogOverlay = React.forwardRef(({ className, ...props }, ref) => (
  <AlertDialogPrimitive.Overlay
    className={cn(
      'fixed inset-0 z-50 bg-black/80 backdrop-blur-sm data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
      className
    )}
    {...props}
    ref={ref}
  />
));
AlertDialogOverlay.displayName = 'AlertDialogOverlay';
AlertDialogOverlay.propTypes = {
  className: PropTypes.string,
  forceMount: PropTypes.bool,
};

const AlertDialogContent = React.forwardRef(({ className, children, ...props }, ref) => (
  <AlertDialogPortal>
    <AlertDialogOverlay />
    <AlertDialogPrimitive.Content
      ref={ref}
      className={cn(
        'fixed left-[50%] top-[50%] z-50 grid w-full max-w-lg translate-x-[-50%] translate-y-[-50%] gap-4 border bg-background p-6 shadow-lg duration-200 sm:rounded-lg',
        'data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%]',
        className
      )}
      {...props}
    >
      {children}
    </AlertDialogPrimitive.Content>
  </AlertDialogPortal>
));
AlertDialogContent.displayName = 'AlertDialogContent';
AlertDialogContent.propTypes = {
  className: PropTypes.string,
  children: PropTypes.node,
  onOpenAutoFocus: PropTypes.func,
  onCloseAutoFocus: PropTypes.func,
  onEscapeKeyDown: PropTypes.func,
  forceMount: PropTypes.bool,
};

const AlertDialogHeader = React.forwardRef(({ className, children, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('flex flex-col gap-2 text-left w-full', className)}
    {...props}
  >
    {children}
  </div>
));
AlertDialogHeader.displayName = 'AlertDialogHeader';
AlertDialogHeader.propTypes = {
  className: PropTypes.string,
  children: PropTypes.node,
};

const AlertDialogFooter = React.forwardRef(({ className, children, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('flex flex-row justify-end gap-2 w-full', className)}
    {...props}
  >
    {children}
  </div>
));
AlertDialogFooter.displayName = 'AlertDialogFooter';
AlertDialogFooter.propTypes = {
  className: PropTypes.string,
  children: PropTypes.node,
};

const AlertDialogTitle = React.forwardRef(({ className, children, ...props }, ref) => (
  <AlertDialogPrimitive.Title
    ref={ref}
    className={cn(
      'text-lg font-semibold leading-none tracking-tight',
      className
    )}
    {...props}
  >
    {children}
  </AlertDialogPrimitive.Title>
));
AlertDialogTitle.displayName = 'AlertDialogTitle';
AlertDialogTitle.propTypes = {
  className: PropTypes.string,
  children: PropTypes.node,
};

const AlertDialogDescription = React.forwardRef(({ className, children, ...props }, ref) => (
  <AlertDialogPrimitive.Description
    ref={ref}
    className={cn(
      'text-sm text-muted-foreground',
      className
    )}
    {...props}
  >
    {children}
  </AlertDialogPrimitive.Description>
));
AlertDialogDescription.displayName = 'AlertDialogDescription';
AlertDialogDescription.propTypes = {
  className: PropTypes.string,
  children: PropTypes.node,
};

const AlertDialogAction = React.forwardRef(({ className, children, ...props }, ref) => (
  <AlertDialogPrimitive.Action
    ref={ref}
    className={cn(buttonVariants(), className)}
    {...props}
  >
    {children}
  </AlertDialogPrimitive.Action>
));
AlertDialogAction.displayName = 'AlertDialogAction';
AlertDialogAction.propTypes = {
  className: PropTypes.string,
  children: PropTypes.node,
};

const AlertDialogCancel = React.forwardRef(({ className, children, ...props }, ref) => (
  <AlertDialogPrimitive.Cancel
    ref={ref}
    className={cn(buttonVariants({ variant: 'outline' }), className)}
    {...props}
  >
    {children}
  </AlertDialogPrimitive.Cancel>
));
AlertDialogCancel.displayName = 'AlertDialogCancel';
AlertDialogCancel.propTypes = {
  className: PropTypes.string,
  children: PropTypes.node,
};

export {
  AlertDialog,
  AlertDialogPortal,
  AlertDialogOverlay,
  AlertDialogTrigger,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogAction,
  AlertDialogCancel,
};
